import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Box, 
  Container,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HubIcon from '@mui/icons-material/Hub';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

const mainNavItems = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Platform', path: '/platform' },
  { name: 'Solutions', path: '/solutions' },
  { name: 'Ministries', path: '/ministries' },
  { name: 'Use Cases', path: '/use-cases' },
];

const secondaryNavItems = [
  { name: 'Insights', path: '/insights' },
  { name: 'Partners', path: '/partners' },
  { name: 'Careers', path: '/careers' },
  { name: 'Technology', path: '/technology' },
  { name: 'Case Studies', path: '/case-studies' },
];

const allNavItems = [...mainNavItems, ...secondaryNavItems];

const Logo = ({ scrolled }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1.2, 
      cursor: 'pointer',
    }}
  >
    <Box 
      sx={{ 
        width: 30, 
        height: 30, 
        borderRadius: '6px', 
        bgcolor: 'primary.main', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        boxShadow: '0 0 15px rgba(0, 124, 255, 0.4)',
      }}
    >
      <HubIcon sx={{ color: '#fff', fontSize: '1rem' }} />
    </Box>
    <Typography
      variant="h5"
      sx={{ 
        fontWeight: 900, 
        color: scrolled ? 'text.primary' : '#FFFFFF', 
        letterSpacing: '-0.5px',
        fontSize: '1.3rem',
      }}
    >
      Niti<Box component="span" sx={{ color: 'primary.main' }}>X</Box>
    </Typography>
  </Box>
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const isHome = location.pathname === '/';
  const shouldBeSolid = scrolled || !isHome;

  const drawer = (
    <Box sx={{ p: 3, pt: 2, bgcolor: 'background.default', height: '100%', color: 'text.primary' }}>
      <IconButton 
        onClick={handleDrawerToggle} 
        sx={{ position: 'absolute', right: 16, top: 16, color: 'text.primary' }}
      >
        <CloseIcon />
      </IconButton>
      
      <Box sx={{ mt: 4, mb: 6 }} onClick={() => navigate('/')}>
        <Logo scrolled={true} />
      </Box>

      <List>
        {allNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              key={item.name} 
              disablePadding 
              sx={{ 
                mb: 1,
                bgcolor: isActive ? 'rgba(0, 124, 255, 0.08)' : 'transparent',
                borderRadius: '8px',
                borderLeft: isActive ? '4px solid #007CFF' : '4px solid transparent'
              }}
            >
              <NavLink
                to={item.path}
                onClick={handleDrawerToggle}
                style={{
                  textDecoration: 'none',
                  color: isActive ? '#007CFF' : '#0F172A',
                  fontWeight: isActive ? 800 : 500,
                  fontSize: '1rem',
                  padding: '12px 16px',
                  display: 'block',
                  width: '100%'
                }}
              >
                <ListItemText primary={item.name} />
              </NavLink>
            </ListItem>
          );
        })}
      </List>
      <Button 
        variant="contained" 
        fullWidth 
        sx={{ mt: 4, bgcolor: '#007CFF', color: '#fff', fontWeight: 800, borderRadius: '8px' }} 
        onClick={() => { navigate('/contact'); handleDrawerToggle(); }}
      >
        Request Demo
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: shouldBeSolid ? '#FFFFFF' : 'transparent',
          boxShadow: shouldBeSolid ? '0 10px 40px rgba(0,0,0,0.05)' : 'none',
          backdropFilter: shouldBeSolid ? 'blur(20px)' : 'none',
          transition: 'all 0.3s ease',
          py: scrolled ? 0.5 : 1, 
          borderBottom: shouldBeSolid ? '1px solid rgba(0,0,0,0.05)' : 'none'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', minHeight: scrolled ? 55 : 65 }}>
            <Box onClick={() => navigate('/')}>
              <Logo scrolled={shouldBeSolid} />
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
              {mainNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    style={{
                      textDecoration: 'none',
                      position: 'relative',
                      color: isActive ? '#007CFF' : (shouldBeSolid ? '#0F172A' : '#FFFFFF'), 
                      fontWeight: isActive ? 800 : 600,
                      fontSize: '0.82rem',
                      padding: '8px 12px',
                      transition: '0.3s',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    {item.name}
                    {isActive && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          bottom: -10, 
                          width: '50%', 
                          height: 2, 
                          bgcolor: '#007CFF',
                          borderRadius: '1px'
                        }} 
                      />
                    )}
                  </NavLink>
                );
              })}
              
              <Button
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleMenuOpen}
                sx={{ 
                  color: shouldBeSolid ? '#0F172A' : '#FFFFFF', 
                  fontWeight: 600, 
                  fontSize: '0.82rem', 
                  ml: 1,
                  textTransform: 'none',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                More
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    bgcolor: '#FFFFFF',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    mt: 1.5,
                    minWidth: 180,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
                    '& .MuiMenuItem-root': {
                      py: 1,
                      px: 2,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#0F172A',
                      '&:hover': { bgcolor: 'rgba(0, 124, 255, 0.05)', color: 'primary.main' }
                    }
                  }
                }}
              >
                {secondaryNavItems.map((item) => (
                  <MenuItem 
                    key={item.name} 
                    onClick={() => { navigate(item.path); handleMenuClose(); }}
                    sx={{ color: location.pathname === item.path ? '#007CFF !important' : 'inherit' }}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button 
                variant="contained" 
                sx={{ 
                  display: { xs: 'none', md: 'flex' },
                  bgcolor: '#007CFF',
                  color: '#fff',
                  borderRadius: '6px',
                  px: 2.5,
                  py: 0.8,
                  fontWeight: 800,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#005AC2' }
                }}
                onClick={() => navigate('/contact')}
              >
                Request Demo
              </Button>
              <IconButton
                color="inherit"
                onClick={handleDrawerToggle}
                sx={{ display: { lg: 'none' } }}
              >
                <MenuIcon sx={{ color: shouldBeSolid ? '#0F172A' : '#FFFFFF' }} />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ 
          '& .MuiDrawer-paper': { width: '100%', maxWidth: 300, bgcolor: 'background.paper' },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
